import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ToasterService } from 'angular2-toaster';
import { WebServiceService } from '../../shared/services/web-service.service';
import { LocalStorageService } from '../../shared/services/storage/local-storage.service';

@Component({
  selector: 'app-direct-invoice-adjustment',
  templateUrl: './direct-invoice-adjustment.component.html',
  styleUrls: ['./direct-invoice-adjustment.component.css'],
  providers: [WebServiceService]
})
export class DirectInvoiceAdjustmentComponent {
  @Input() toggleEnabled: boolean = false;
  @Input() netPayable: any = 0;
  @Input() dateFormat: string = 'dd-MM-yyyy';
  @Input() themeBlockColor: string = '';
  @Input() themeTextColor: string = '';
  @Input() readOnly: boolean = false;
  @Output() adjustmentApplied = new EventEmitter<void>();

  @ViewChild('adjustmentDialog') adjustmentDialog: any;
  @ViewChild('saveWarningDialog') saveWarningDialog: any;

  hasEligibleDocuments: boolean = false;
  adjustmentTouched: boolean = false;
  skippedAdjustment: boolean = false;
  loading: boolean = false;
  vendorName: string = '';
  vendorCode: string = '';
  vendorLocationName: string = '';
  documents: any[] = [];
  draftDocuments: any[] = [];
  appliedAdjustments: any[] = [];
  confirmedAdjustments: any[] = [];
  adhocAdvanceAdjustedAmount: number = 0;
  debitNoteAdjustedAmount: number = 0;
  totalAdjustedAmount: number = 0;
  draftTotalApplied: number = 0;

  private vendorId: any = '';
  private invoiceId: any = 0;
  private loadedSummaryTotal: number = 0;

  constructor(
    private webService: WebServiceService,
    private _localStorageService: LocalStorageService,
    private toasterService: ToasterService
  ) {}

  get displayAdjustedTotal(): number {
    if (this.adjustmentTouched) {
      return this.totalAdjustedAmount;
    }
    return this.loadedSummaryTotal > 0 ? this.loadedSummaryTotal : this.savedSummaryTotal;
  }

  get displayAdhocAmount(): number {
    if (this.adjustmentTouched) {
      return this.adhocAdvanceAdjustedAmount;
    }
    if (this.loadedSummaryTotal > 0 || this.loadedAdhocAmount > 0) {
      return this.loadedAdhocAmount;
    }
    return this.savedAdhocAmount;
  }

  get displayDebitAmount(): number {
    if (this.adjustmentTouched) {
      return this.debitNoteAdjustedAmount;
    }
    if (this.loadedSummaryTotal > 0 || this.loadedDebitAmount > 0) {
      return this.loadedDebitAmount;
    }
    return this.savedDebitAmount;
  }

  get balancePayable(): number {
    const balance = this.toNumber(this.netPayable) - this.draftTotalApplied;
    return balance < 0 ? 0 : this.round2(balance);
  }

  private loadedAdhocAmount: number = 0;
  private loadedDebitAmount: number = 0;
  private savedAdhocAmount: number = 0;
  private savedDebitAmount: number = 0;
  private savedSummaryTotal: number = 0;
  private savedVendorId: string = '';

  load(params: any) {
    const vendorId = params && params.vendorId ? params.vendorId : '';
    const invoiceId = params && params.invoiceId ? params.invoiceId : 0;
    const clearPrevious = !!(params && params.clearPrevious);
    const openAfter = !!(params && params.openAfter);
    const vendorChanged = String(vendorId) !== String(this.vendorId);
    if (clearPrevious || (vendorChanged && this.vendorId)) {
      if (!this.savedVendorId || String(vendorId) !== String(this.savedVendorId)) {
        this.savedAdhocAmount = 0;
        this.savedDebitAmount = 0;
        this.savedSummaryTotal = 0;
        this.savedVendorId = '';
      }
      this.resetConfirmed();
    } else if (vendorChanged) {
      this.resetConfirmed();
    }
    this.vendorId = vendorId;
    this.invoiceId = invoiceId;
    if (params && params.netPayable != null && params.netPayable !== '') {
      this.netPayable = params.netPayable;
    }
    if (!vendorId) {
      this.hasEligibleDocuments = false;
      this.documents = [];
      this.loadedSummaryTotal = 0;
      this.loadedAdhocAmount = 0;
      this.loadedDebitAmount = 0;
      return;
    }
    const requestObj = {
      company_id: this._localStorageService.getEmpCompanyId() == undefined ? 0 : this._localStorageService.getEmpCompanyId(),
      user_id: this._localStorageService.getUserId() == undefined ? 0 : this._localStorageService.getUserId(),
      vendor_id: vendorId,
      invoice_id: invoiceId ? invoiceId : 0,
      net_payable_before_adjustment: this.toNumber(this.netPayable)
    };
    this.loading = true;
    this.webService.commonPostMethod('Invoices/ajax_get_direct_invoice_adjustable_documents', requestObj).subscribe(
      data => {
        this.loading = false;
        const response = data && data.status && data.response ? data.response : {};
        const vendor = response.vendor ? response.vendor : {};
        this.vendorName = vendor.vendor_name ? vendor.vendor_name : '';
        this.vendorCode = vendor.vendor_code ? vendor.vendor_code : '';
        this.vendorLocationName = vendor.vendor_location_name ? vendor.vendor_location_name : '';
        this.hasEligibleDocuments = response.has_eligible_documents === '1' || response.has_eligible_documents === 1;
        this.documents = response.documents ? response.documents : [];
        this.appliedAdjustments = response.applied_adjustments ? response.applied_adjustments : [];
        const summary = response.summary ? response.summary : {};
        if (!this.adjustmentTouched) {
          this.loadedAdhocAmount = this.toNumber(summary.adhoc_advance_adjusted_amount);
          this.loadedDebitAmount = this.toNumber(summary.debit_note_adjusted_amount);
          this.loadedSummaryTotal = this.toNumber(summary.total_adjusted_amount);
          if (this.loadedSummaryTotal <= 0 && this.appliedAdjustments.length > 0) {
            this.loadedAdhocAmount = 0;
            this.loadedDebitAmount = 0;
            for (let row of this.appliedAdjustments) {
              const amount = this.toNumber(row.amount_applied_base);
              if (row.document_type === 'debit_note') {
                this.loadedDebitAmount += amount;
              } else if (row.document_type === 'adhoc_advance') {
                this.loadedAdhocAmount += amount;
              }
            }
            this.loadedSummaryTotal = this.loadedAdhocAmount + this.loadedDebitAmount;
          }
        }
        if (!this.hasEligibleDocuments && this.documents.length > 0) {
          this.hasEligibleDocuments = true;
        }
        if (openAfter) {
          this.openPopup();
        }
      },
      () => {
        this.loading = false;
        this.hasEligibleDocuments = false;
      }
    );
  }

  openPopup() {
    if (this.readOnly) {
      this.draftDocuments = this.buildDraft(false);
    } else {
      this.draftDocuments = this.buildDraft(true);
    }
    this.recalcDraft();
    if (this.adjustmentDialog) {
      this.adjustmentDialog.open();
    }
  }

  openSaveWarning() {
    setTimeout(() => {
      if (this.saveWarningDialog && typeof this.saveWarningDialog.open === 'function') {
        this.saveWarningDialog.open();
      }
    }, 0);
  }

  closeSaveWarning(dialog?: any) {
    this.closeDialog(dialog || this.saveWarningDialog);
  }

  dismissSaveWarning(dialog?: any) {
    this.skippedAdjustment = true;
    this.closeDialog(dialog || this.saveWarningDialog);
  }

  cancelAdjustment(dialog?: any) {
    this.draftDocuments = [];
    this.closeDialog(dialog);
    this.toasterService.pop('info', 'Info', 'Cancelled. No change was applied to the invoice.');
  }

  dontAdjust(dialog?: any) {
    this.skippedAdjustment = true;
    this.draftDocuments = [];
    this.closeDialog(dialog);
    this.toasterService.pop('info', 'Info', 'No advance or debit note will be adjusted. You can continue and save the invoice.');
  }

  applyAdjustments(dialog?: any) {
    for (let doc of this.draftDocuments) {
      if (!doc.selected) {
        continue;
      }
      const amount = this.toNumber(doc.amount_applied_base);
      const unadjusted = this.toNumber(doc.unadjusted_amount_base);
      if (amount <= 0) {
        this.toasterService.pop('info', 'Info', 'Amount Applied must be greater than zero for selected documents');
        return;
      }
      if (amount > unadjusted + 0.001) {
        this.toasterService.pop('info', 'Info', 'Amount Applied cannot exceed Unadjusted Amount');
        return;
      }
    }
    if (this.draftTotalApplied > this.toNumber(this.netPayable) + 0.001) {
      this.toasterService.pop('error', 'Error', 'Total applied amount cannot exceed net payable before adjustment');
      return;
    }
    const rows = [];
    let adhoc = 0;
    let debit = 0;
    for (let doc of this.draftDocuments) {
      if (!doc.selected) {
        continue;
      }
      const amount = this.round2(this.toNumber(doc.amount_applied_base));
      if (amount <= 0) {
        continue;
      }
      if (doc.document_type === 'debit_note') {
        debit += amount;
      } else if (doc.document_type === 'adhoc_advance') {
        adhoc += amount;
      }
      rows.push({
        document_type: doc.document_type,
        document_id: doc.document_id,
        amount_applied_base: amount,
        remarks: doc.remarks ? doc.remarks : ''
      });
    }
    this.confirmedAdjustments = rows;
    this.adhocAdvanceAdjustedAmount = this.round2(adhoc);
    this.debitNoteAdjustedAmount = this.round2(debit);
    this.totalAdjustedAmount = this.round2(adhoc + debit);
    this.adjustmentTouched = true;
    this.skippedAdjustment = false;
    this.closeDialog(dialog);
    this.closeDialog(this.saveWarningDialog);
    this.toasterService.pop('success', 'Success', 'Adjustment applied. Total Payable is updated.');
    this.adjustmentApplied.emit();
  }

  private closeDialog(dialog?: any) {
    const target = dialog || this.adjustmentDialog;
    if (target && typeof target.close === 'function') {
      target.close();
    }
  }

  onSelectChange(doc: any) {
    if (!doc.selected) {
      doc.amount_applied_base = 0;
    } else {
      doc.amount_applied_base = this.maxApplicable(doc, this.toNumber(doc.unadjusted_amount_base));
    }
    this.recalcDraft();
  }

  onAmountChange(doc: any) {
    let amount = this.toNumber(doc.amount_applied_base);
    if (amount < 0) {
      amount = 0;
    }
    const unadjusted = this.toNumber(doc.unadjusted_amount_base);
    if (amount > unadjusted) {
      amount = unadjusted;
      this.toasterService.pop('info', 'Info', 'Amount cannot exceed unadjusted amount');
    }
    const capped = this.maxApplicable(doc, amount);
    if (capped < amount) {
      amount = capped;
      this.toasterService.pop('info', 'Info', 'Amount cannot exceed the net payable still available for adjustment');
    }
    doc.amount_applied_base = amount;
    this.recalcDraft();
  }

  buildPayload(invoiceId: any): any[] {
    const id = invoiceId ? invoiceId : 0;
    return (this.confirmedAdjustments || []).map(row => ({
      invoice_id: id,
      document_type: row.document_type,
      document_id: row.document_id,
      amount_applied_base: row.amount_applied_base,
      remarks: row.remarks ? row.remarks : ''
    }));
  }

  /** JE / preview: confirmed after Adjust, else previously applied rows. */
  buildJournalPayload(invoiceId: any): any[] {
    const id = invoiceId ? invoiceId : 0;
    const rows = this.adjustmentTouched ? (this.confirmedAdjustments || []) : (this.appliedAdjustments || []);
    return (rows || []).map(row => ({
      invoice_id: id,
      document_type: row.document_type,
      document_id: row.document_id,
      amount_applied_base: row.amount_applied_base,
      remarks: row.remarks ? row.remarks : ''
    }));
  }

  seedFromInvoice(invoice: any) {
    if (!invoice || this.adjustmentTouched) {
      return;
    }
    const summary = invoice.adjustment_summary ? invoice.adjustment_summary : {};
    const adhoc = this.toNumber(summary.adhoc_advance_adjusted_amount != null ? summary.adhoc_advance_adjusted_amount : invoice.adhoc_advance_adjusted_amount);
    const debit = this.toNumber(summary.debit_note_adjusted_amount != null ? summary.debit_note_adjusted_amount : invoice.debit_note_adjusted_amount);
    let total = this.toNumber(summary.total_adjusted_amount);
    if (total <= 0) {
      total = this.round2(adhoc + debit);
    }
    this.savedAdhocAmount = adhoc;
    this.savedDebitAmount = debit;
    this.savedSummaryTotal = total;
    this.savedVendorId = invoice.vendor_id ? String(invoice.vendor_id) : '';
    this.loadedAdhocAmount = adhoc;
    this.loadedDebitAmount = debit;
    this.loadedSummaryTotal = total;
    if (invoice.payment_adjustments && invoice.payment_adjustments.length) {
      this.appliedAdjustments = invoice.payment_adjustments;
    }
  }

  shouldWarnBeforeSave(): boolean {
    if (!this.hasEligibleDocuments || this.adjustmentTouched || this.skippedAdjustment) {
      return false;
    }
    if (this.invoiceId && this.displayAdjustedTotal > 0) {
      return false;
    }
    return true;
  }

  documentTypeLabel(documentType: string): string {
    if (documentType === 'debit_note') {
      return 'Debit Note';
    }
    if (documentType === 'adhoc_advance') {
      return 'Ad-hoc Advance';
    }
    return documentType ? documentType : '';
  }

  datePipeFormat(): string {
    const format = this.dateFormat ? this.dateFormat : 'dd-MM-yyyy';
    return format.replace('mm', 'MM');
  }

  private buildDraft(editable: boolean): any[] {
    const source = JSON.parse(JSON.stringify(this.documents || []));
    const confirmed = this.adjustmentTouched ? this.confirmedAdjustments : this.appliedAdjustments;
    const confirmedMap = {};
    for (let row of confirmed || []) {
      confirmedMap[String(row.document_type) + '_' + String(row.document_id)] = row;
    }
    const hasPrior = (confirmed || []).length > 0 || this.adjustmentTouched;
    let remaining = this.toNumber(this.netPayable);
    for (let doc of source) {
      doc.vendor_name = this.vendorName;
      const key = String(doc.document_type) + '_' + String(doc.document_id);
      const prior = confirmedMap[key];
      if (prior) {
        doc.selected = true;
        doc.amount_applied_base = this.toNumber(prior.amount_applied_base);
        doc.remarks = prior.remarks ? prior.remarks : '';
        remaining -= this.toNumber(doc.amount_applied_base);
      } else if (!hasPrior && editable) {
        const unadjusted = this.toNumber(doc.unadjusted_amount_base);
        const apply = Math.min(unadjusted, Math.max(remaining, 0));
        doc.selected = apply > 0;
        doc.amount_applied_base = apply > 0 ? this.round2(apply) : 0;
        doc.remarks = '';
        remaining -= apply;
      } else {
        doc.selected = false;
        doc.amount_applied_base = 0;
        doc.remarks = '';
      }
    }
    return source;
  }

  private recalcDraft() {
    let total = 0;
    for (let doc of this.draftDocuments) {
      if (doc.selected) {
        total += this.toNumber(doc.amount_applied_base);
      }
    }
    this.draftTotalApplied = this.round2(total);
  }

  private maxApplicable(doc: any, requested: number): number {
    let others = 0;
    for (let row of this.draftDocuments) {
      if (row !== doc && row.selected) {
        others += this.toNumber(row.amount_applied_base);
      }
    }
    const room = this.toNumber(this.netPayable) - others;
    const unadjusted = this.toNumber(doc.unadjusted_amount_base);
    const cap = Math.min(unadjusted, Math.max(room, 0));
    return this.round2(Math.min(requested, cap));
  }

  private resetConfirmed() {
    this.adjustmentTouched = false;
    this.skippedAdjustment = false;
    this.confirmedAdjustments = [];
    this.adhocAdvanceAdjustedAmount = 0;
    this.debitNoteAdjustedAmount = 0;
    this.totalAdjustedAmount = 0;
    this.loadedAdhocAmount = 0;
    this.loadedDebitAmount = 0;
    this.loadedSummaryTotal = 0;
    this.appliedAdjustments = [];
  }

  toNumber(value: any): number {
    const parsed = parseFloat(this.webService.convertNaN(value));
    return isNaN(parsed) ? 0 : parsed;
  }

  private round2(value: number): number {
    return Math.round((value + 0.0000001) * 100) / 100;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1914368402/src/src/app/generic-ui-components/direct-invoice-adjustment/direct-invoice-adjustment.component.ts